/**
 * The support matrix, the parse floor beneath it, and what an unsupported browser is told
 * (`PLT-008` AC-1, AC-2).
 *
 * ## Why the matrix is code rather than a page in a wiki
 *
 * `MEM-043` AC-7 deferred this deliberately — *"supported platforms follow the `SYS` matrix"* —
 * and `supported-platforms.ts` records why writing one there would have been worse than having
 * none: a second, unowned copy for the real matrix to contradict. `PLT-008` AC-1 is the story
 * that carries it, and the way to carry it without recreating that problem is to have exactly
 * one copy. {@link SUPPORTED_PLATFORMS} is it. `docs/standards/supported-browsers.md` renders
 * the same rows, and `the-support-matrix.test.ts` fails if the document and this list disagree by
 * a single character.
 *
 * ## Two floors, not one, because they mean different things
 *
 * A support matrix with one line in it says "we support these and you are on your own otherwise",
 * which is exactly what AC-2 forbids. So there are two:
 *
 *   - {@link SUPPORTED_PLATFORMS} — where the product is **tested and expected to be right**.
 *     Everything works: install, offline, push where the platform allows it, the scanner, the
 *     whole shell.
 *   - {@link BUNDLE_PARSE_FLOOR} — the oldest engines the shipped JavaScript **parses** on at
 *     all. Between the two floors is a real band of browsers (Chrome 87–102, Safari 14–16.3,
 *     Firefox 78–102) where the app runs, looks slightly wrong in places, and **reads,
 *     registers and checks out** — which is AC-2 exactly.
 *
 * Below the parse floor there is nothing to promise. A bundle an engine cannot parse does not
 * render a degraded experience; it renders a blank page. Saying so in the matrix is more useful
 * than a promise nobody could keep, and it is the reason the floor is pinned in `vite.config.ts`
 * from this file rather than left to whatever the current Vite major happens to default to —
 * Vite 5's `'modules'` target *is* this list, and Vite 6 changed the default. A silent bump of
 * the parse floor by a dependency upgrade is a support matrix that quietly stopped being true.
 *
 * ## What "unsupported" is allowed to cost, and what it may never cost
 *
 * `PLT-008`'s Notes are the ruling: *"Matching, the connect surface and the scanner may
 * reasonably require a current browser; those three may not."* {@link NEVER_BLOCKED_JOURNEYS}
 * names the three, and the assessment below is **advisory by construction** — it produces a
 * verdict and a sentence, and nothing in this application branches on it except the notice that
 * says the sentence. A gate is one `if` away, which is why the test scans for one.
 *
 * ## Capabilities, not user agents
 *
 * The runtime check is feature detection, in the house style `supported-platforms.ts` set: a
 * member on a browser nobody has ruled on gets the answer their browser actually gives. The
 * matrix names versions because a member reading a support page needs a version to compare
 * against; the code never reads one. The one user-agent test in this slice is
 * `isAppleTouchPlatform`, and it is over there under protest for a reason that does not apply
 * here.
 */

/** A row of the matrix. One operating system, one browser, one floor, one reason for it. */
export interface SupportedPlatform {
  /** The operating system, as a member would name it. */
  readonly os: string;
  /** The browser, as a member would name it. */
  readonly browser: string;
  /** The oldest version the product is tested on. */
  readonly from: string;
  /** What sets that floor. A version with no reason attached is a version nobody can revise. */
  readonly reason: string;
}

/**
 * **The support matrix** (`PLT-008` AC-1).
 *
 * Each floor is the version at which the last capability this product depends on arrived, rather
 * than a round number or a "last two versions" formula. "Last two versions" cannot be written
 * down — it names a different set every six weeks and is therefore not a statement a member can
 * check themselves against.
 *
 * `Chromium 103` is where `backdrop-filter` stopped needing a prefix and where the app's sticky
 * header and tab bar became legible over scrolled content; `Firefox 103` is the same capability
 * on that engine, which is why the two numbers coincide. `Safari 16.4` is web push — the one
 * platform capability that is not a nicety, because on iOS it is the *only* way a member is told
 * their matches have landed, and it arrived with the installed-app requirement `PLT-008` AC-6 is
 * about.
 */
export const SUPPORTED_PLATFORMS: ReadonlyArray<SupportedPlatform> = [
  {
    os: 'iOS / iPadOS',
    browser: 'Safari',
    from: '16.4',
    reason: 'Web push, which on iOS arrives only from an installed app (AC-6)',
  },
  {
    os: 'Android 10 and later',
    browser: 'Chrome',
    from: '103',
    reason: 'Unprefixed backdrop-filter, used by the sticky header and the tab bar',
  },
  {
    os: 'Windows 10 and later',
    browser: 'Chrome, Edge',
    from: '103',
    reason: 'Unprefixed backdrop-filter, used by the sticky header and the tab bar',
  },
  {
    os: 'macOS 12 and later',
    browser: 'Safari',
    from: '16.4',
    reason: 'One Safari floor rather than two, so a member has one number to check',
  },
  {
    os: 'Windows, macOS, Android',
    browser: 'Firefox',
    from: '103',
    reason: 'Unprefixed backdrop-filter; Firefox does not install web apps and is not offered to',
  },
];

/**
 * The oldest engines the shipped bundle parses on, pinned into `vite.config.ts` from here.
 *
 * This is Vite 5's `'modules'` target written out. It is written out because a default is not a
 * decision: an engine list that moves when a build tool is upgraded is a support matrix that
 * became untrue in a lockfile diff. Below this line the app does not degrade, it fails to start,
 * and the matrix says so rather than implying otherwise.
 */
export const BUNDLE_PARSE_FLOOR: ReadonlyArray<string> = [
  'es2020',
  'edge88',
  'firefox78',
  'chrome87',
  'safari14',
];

/**
 * The journeys an unsupported browser must still complete (`PLT-008` AC-2).
 *
 * Named as journeys with routes attached rather than as prose, so the claim is checkable: the
 * test resolves each `route` against `App.tsx` and fails if one of the three has quietly stopped
 * existing or moved behind something that could refuse it.
 *
 * The three are the story's, and the reasoning is the story's too: a member blocked from reading
 * is a member who never registers, and a member blocked at checkout is money that does not
 * arrive. Everything else — matching, Connect, the scanner — may reasonably want a current
 * browser.
 */
export const NEVER_BLOCKED_JOURNEYS = [
  { id: 'reading', label: 'Reading an event', route: '/app/events/:eventId' },
  { id: 'registration', label: 'Creating an account', route: '/app/join' },
  { id: 'checkout', label: 'Paying for a ticket', route: '/app/events/:eventId/checkout' },
] as const;

/**
 * The capabilities the assessment probes, and what each one costs a member when it is missing.
 *
 * Deliberately short, and deliberately **not** a list of everything the app touches. A capability
 * belongs here when its absence makes a screen wrong; a capability whose absence makes a
 * *feature* unavailable belongs in that feature's own check, where it already is —
 * `canRunScanner` gates on the camera, `isPushSupported` gates on push, and neither one may make
 * a browser "unsupported". Push especially: `MEM-040` AC-5 is that a member without it loses push
 * and loses nothing else, and folding it in here would turn every Firefox member into a member
 * being told their browser is wrong.
 */
export const PROBED_CAPABILITIES = [
  {
    id: 'service-worker',
    label: 'Service workers',
    cost: 'The app cannot be installed and will not open without a connection',
  },
  {
    id: 'css-focus-visible',
    label: 'CSS :focus-visible',
    cost: 'Keyboard focus is invisible on every control',
  },
  {
    id: 'css-backdrop-filter',
    label: 'CSS backdrop-filter',
    cost: 'The header and the tab bar sit unreadably over scrolled content',
  },
  {
    id: 'intl-format-to-parts',
    label: 'Intl.DateTimeFormat.formatToParts',
    cost: 'Event times cannot be rendered in the event’s own timezone',
  },
  {
    id: 'resize-observer',
    label: 'ResizeObserver',
    cost: 'Panels that size themselves to their content are drawn at the wrong height',
  },
] as const;

export type ProbedCapability = (typeof PROBED_CAPABILITIES)[number]['id'];

/** What the browser answered for each probe. Every key is required; a missing one is a bug. */
export type BrowserProbe = Readonly<Record<ProbedCapability, boolean>>;

export interface BrowserSupportVerdict {
  /** Every probed capability is present. */
  readonly supported: boolean;
  /** The ones that are not, in declaration order. Empty when `supported`. */
  readonly missing: ReadonlyArray<ProbedCapability>;
}

/**
 * The verdict, as a pure function of the probe.
 *
 * Pure so the unsupported case can be asserted without an unsupported browser to run it on —
 * which matters here more than usual, because the whole point of AC-2 is behaviour on a browser
 * nobody in the team has.
 */
export function assessBrowserSupport(probe: BrowserProbe): BrowserSupportVerdict {
  const missing = PROBED_CAPABILITIES.map((capability) => capability.id).filter(
    (id) => !probe[id],
  );
  return { supported: missing.length === 0, missing };
}

/**
 * Asks the current browser the five questions.
 *
 * Every probe is written to answer `false` rather than throw on an engine that has never heard of
 * the thing being asked about — `CSS.supports` is itself a capability, and an old enough browser
 * has no `CSS` object to ask.
 */
export function readBrowserProbe(): BrowserProbe {
  const supportsCss = (declaration: string): boolean => {
    try {
      return typeof CSS !== 'undefined' && CSS.supports(declaration);
    } catch {
      return false;
    }
  };

  return {
    'service-worker': typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    'css-focus-visible': supportsCss('selector(:focus-visible)'),
    'css-backdrop-filter': supportsCss('backdrop-filter: blur(1px)'),
    'intl-format-to-parts':
      typeof Intl !== 'undefined' &&
      typeof Intl.DateTimeFormat.prototype.formatToParts === 'function',
    'resize-observer': typeof window !== 'undefined' && 'ResizeObserver' in window,
  };
}

/**
 * What an unsupported browser is told.
 *
 * **The first sentence is the reassurance, and it is first on purpose.** A member who reads
 * "your browser is not supported" and nothing else concludes they cannot buy a ticket, and the
 * one thing AC-2 is about is that they can. So the notice leads with what still works, names the
 * versions the matrix names so the member has something to act on, and does not enumerate what
 * might look wrong — a list of maybes is a list nobody can check.
 *
 * It is dismissible, and dismissal is remembered. A member who has read it once and decided to
 * carry on has answered the question; asking again on every screen is how a notice becomes
 * something people learn to look past.
 */
export const UNSUPPORTED_BROWSER_TITLE = 'Some things may not look right on this browser';
export const UNSUPPORTED_BROWSER_BODY =
  'Reading events, registering and paying all work here. H2HSpark is tested on Safari 16.4 and ' +
  'later on iPhone, iPad and Mac, and on Chrome, Edge and Firefox 103 and later.';
export const UNSUPPORTED_BROWSER_DISMISS_LABEL = 'Got it';
